import { includes } from 'lodash-es';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';

import type { RootState } from 'store';

import { POLICY_PATH } from 'common/accountActivationAndRecovery/AccountScreen';
import { userMustAcceptPrivacyPolicy } from 'common/auth/privacyPolicyAcceptance';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { AuthUserType, BaseRoles, RoleVariantEnum, UserSubType } from 'common/graphql/types';
import { isSponsorUser, SPONSOR_HOME_PATH } from 'common/utils/sponsorUtils';
import { isSecurityQuestionMissing, isSiteRoleBySubType } from 'module/auth/login/login.utils';

const STUDY_ROLES = [
    RoleVariantEnum.StudyBuilder,
    RoleVariantEnum.StudySupport,
    RoleVariantEnum.ClinicalTrialLead,
    RoleVariantEnum.BlindedStudyManager,
    RoleVariantEnum.AdminReadOnly,
];

export const AuthUserLandingPage = (): ReactElement => {
    const auth = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const { activeUser, activeStudySite } = auth || {};

    const isOktaLogin =
        featureFlags.fxIntegration &&
        activeUser?.userType !== AuthUserType.PATIENT &&
        activeUser?.authenticationMethod === 'OKTA';

    if (isSecurityQuestionMissing(activeUser) && !isOktaLogin) {
        return <Navigate to="/activate" replace />;
    }

    if (activeUser.userType === AuthUserType.PATIENT) {
        return <Navigate to="/patientDashboard" replace />;
    }

    if (userMustAcceptPrivacyPolicy(activeUser)) {
        return <Navigate to={POLICY_PATH} replace />;
    }

    if (
        featureFlags.fxIntegration &&
        !auth.activeUser.currentUserSubscriber?.currentRoleId &&
        auth.activeUser.currentUserSubscriber?.roleClaims?.length
    ) {
        return <Navigate to="/selectRoleView" replace />;
    }

    if (
        isSiteRoleBySubType(activeUser?.subType) ||
        (!featureFlags.fxIntegration && activeUser.subType === UserSubType.PRODUCT_SUPPORT)
    ) {
        if (
            !activeStudySite.activeStudySiteReady &&
            !!activeUser?.userStudies?.length &&
            !includes(location.search, 'studyId')
        ) {
            return <Navigate to="/activestudysite" replace />;
        }
        const homePath = isSponsorUser(activeUser) ? SPONSOR_HOME_PATH : `/siteDashboard${location.search}`;

        return <Navigate to={homePath} replace />;
    }

    const isInSiteView = !!auth.activeStudySite?.activeStudy;
    const isStudyRole = includes(STUDY_ROLES, activeUser?.roleVClaim?.sk);
    if (
        (BaseRoles.AdminId === activeUser?.roleBClaim?.sk && !includes(activeUser.permissions, 'Subscriber_Create')) ||
        isStudyRole ||
        (featureFlags.fxIntegration && activeUser?.roleVClaim?.sk === RoleVariantEnum.ProductSupport)
    ) {
        const queryParams = new URLSearchParams(location.search);
        const studyId = queryParams.get('studyId');
        if (studyId) {
            return <Navigate to={`/studydetails/Study.${studyId}`} replace />;
        }
        if (isInSiteView) {
            return <Navigate to="/siteListing" replace />;
        }

        return <Navigate to="/study" replace />;
    }

    // since the subscriber admin and sys admin share the same base role
    if (activeUser?.role === RoleVariantEnum.SystemAdministrator) {
        return <Navigate to="/subscriber" replace />;
    }

    if (RoleVariantEnum.Librarian === activeUser?.roleVClaim?.sk) {
        return <Navigate to="/activitylibrary" replace />;
    }

    if (RoleVariantEnum.TranslationRepresentative === activeUser?.roleVClaim?.sk) {
        return <Navigate to="/userprofile/general" replace />;
    }

    return <Navigate to="/dashboard" replace />;
};
